import React, { useState, useEffect, useMemo } from 'react';
import { Meal, Symptom, MealType, WeeklyInsight, MAIN_SYMPTOMS, Exercise } from '../lib/types';
import { formatIsoDate, formatShortDate, formatTime } from '../lib/dateUtils';
import { getMealFodmapReport } from '../lib/fodmapEngine';
import { localStore } from '../lib/storage';
import { triggerHaptic } from '../lib/haptics';
import {
  ChevronLeft,
  ChevronRight,
  Calendar as CalendarIcon,
  Plus,
  Activity,
  Droplets,
  Sunrise,
  Sun,
  Moon,
  Coffee,
  Utensils,
  Search,
  Filter,
  CheckCircle2,
  X,
  Clock,
  Sparkles,
  Edit3,
  Dumbbell,
  Footprints,
  Bike,
  Zap,
  Waves,
  Trophy,
  Trash2,
} from 'lucide-react';

interface TimelineViewProps {
  meals: Meal[];
  symptoms: Symptom[];
  exercises?: Exercise[];
  insights: WeeklyInsight[];
  onOpenLogMeal?: (type?: MealType) => void;
  onOpenQuickAdd?: (type?: MealType) => void;
  onOpenLogSymptom?: (type?: 'Next-Morning' | 'Post-Meal' | 'General Daily') => void;
  onOpenLogPoop?: () => void;
  onOpenLogExercise?: () => void;
  onEditMeal?: (meal: Meal) => void;
  onEditSymptom?: (symptom: Symptom) => void;
  onEditExercise?: (exercise: Exercise) => void;
  onDeleteMeal?: (id: string) => void;
  onDeleteSymptom?: (id: string) => void;
  onDeleteExercise?: (id: string) => void;
  isCalendarOpen?: boolean;
  onCloseCalendar?: () => void;
  isFilterOpen?: boolean;
  onToggleFilter?: () => void;
  isSearchOpen?: boolean;
  onCloseSearch?: () => void;
}

type TimelinePeriod = 'morning' | 'midday' | 'evening';

interface PeriodItem {
  id: string;
  type: 'meal' | 'symptom' | 'stool' | 'exercise';
  timeMs: number;
  timeStr: string;
  mealData?: Meal;
  symptomData?: Symptom;
  exerciseData?: Exercise;
}

const WEEKDAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export const TimelineView: React.FC<TimelineViewProps> = React.memo(({
  meals,
  symptoms,
  exercises = [],
  insights,
  onOpenLogMeal,
  onOpenQuickAdd,
  onOpenLogSymptom,
  onOpenLogPoop,
  onOpenLogExercise,
  onEditMeal,
  onEditSymptom,
  onEditExercise,
  onDeleteMeal,
  onDeleteSymptom,
  onDeleteExercise,
  isCalendarOpen: externalCalendarOpen,
  onCloseCalendar: externalCloseCalendar,
  isFilterOpen: externalFilterOpen,
  onToggleFilter: externalToggleFilter,
  isSearchOpen: externalSearchOpen,
  onCloseSearch: externalCloseSearch,
}) => {
  const todayIso = useMemo(() => formatIsoDate(new Date()), []);
  const [selectedDate, setSelectedDate] = useState<string>(todayIso);
  const [localCalendarOpen, setLocalCalendarOpen] = useState(false);
  const [localSearchOpen, setLocalSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'meals' | 'symptoms'>('all');
  const [skippedSlots, setSkippedSlots] = useState<Record<string, boolean>>({});
  const [isFodmapEnabled, setIsFodmapEnabled] = useState<boolean>(() => localStore.getFodmapTrackingEnabled());

  useEffect(() => {
    const handler = (e: any) => {
      if (e?.detail?.enabled !== undefined) {
        setIsFodmapEnabled(e.detail.enabled);
      } else {
        setIsFodmapEnabled(localStore.getFodmapTrackingEnabled());
      }
    };
    window.addEventListener('digestlog_fodmap_toggle', handler);
    return () => window.removeEventListener('digestlog_fodmap_toggle', handler);
  }, []);

  const showCalendar = externalCalendarOpen ? true : localCalendarOpen;
  const setShowCalendar = (open: boolean) => {
    setLocalCalendarOpen(open);
    if (!open && externalCloseCalendar) externalCloseCalendar();
  };

  const showSearch = externalSearchOpen ? true : localSearchOpen;
  const setShowSearch = (open: boolean) => {
    setLocalSearchOpen(open);
    if (!open && externalCloseSearch) externalCloseSearch();
  };

  // Parse current selected date object
  const selectedDateObj = useMemo(() => {
    const [y, m, d] = selectedDate.split('-').map(Number);
    return new Date(y, m - 1, d);
  }, [selectedDate]);

  // Calendar month selector state
  const [calendarViewYear, setCalendarViewYear] = useState(() => selectedDateObj.getFullYear());
  const [calendarViewMonth, setCalendarViewMonth] = useState(() => selectedDateObj.getMonth());

  const openFullCalendar = () => {
    triggerHaptic('light');
    const [y, m] = selectedDate.split('-').map(Number);
    setCalendarViewYear(y);
    setCalendarViewMonth(m - 1);
    setShowCalendar(true);
  };

  const handlePrevMonth = () => {
    triggerHaptic('light');
    if (calendarViewMonth === 0) {
      setCalendarViewMonth(11);
      setCalendarViewYear((y) => y - 1);
    } else {
      setCalendarViewMonth((m) => m - 1);
    }
  };

  const handleNextMonth = () => {
    triggerHaptic('light');
    if (calendarViewMonth === 11) {
      setCalendarViewMonth(0);
      setCalendarViewYear((y) => y + 1);
    } else {
      setCalendarViewMonth((m) => m + 1);
    }
  };

  const handleJumpTodayInCalendar = () => {
    triggerHaptic('selection');
    const today = new Date();
    setCalendarViewYear(today.getFullYear());
    setCalendarViewMonth(today.getMonth());
    setSelectedDate(todayIso);
    setShowCalendar(false);
  };

  // Pre-index meals and symptoms by date for calendar dot indicators
  const { mealsByDate, symptomsByDate } = useMemo(() => {
    const mMap = new Map<string, Meal[]>();
    for (const m of meals) {
      const list = mMap.get(m.date) || [];
      list.push(m);
      mMap.set(m.date, list);
    }
    const sMap = new Map<string, Symptom[]>();
    for (const s of symptoms) {
      const list = sMap.get(s.date) || [];
      list.push(s);
      sMap.set(s.date, list);
    }
    return { mealsByDate: mMap, symptomsByDate: sMap };
  }, [meals, symptoms]);

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const firstDayOfCalendarMonth = new Date(calendarViewYear, calendarViewMonth, 1);
  const daysInCalendarMonth = new Date(calendarViewYear, calendarViewMonth + 1, 0).getDate();
  let startDayOfWeek = firstDayOfCalendarMonth.getDay() - 1;
  if (startDayOfWeek === -1) startDayOfWeek = 6;

  // Compute Monday to Sunday week strip surrounding the selected date
  const weekDays = useMemo(() => {
    const current = new Date(selectedDateObj);
    const dayOfWeek = current.getDay(); // 0 is Sunday, 1 is Monday
    // Calculate distance to Monday
    const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    const monday = new Date(current);
    monday.setDate(current.getDate() + diffToMonday);

    const days: { dateIso: string; dayNum: number; dayLabel: string; isPast: boolean; isToday: boolean; isFuture: boolean }[] = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    for (let i = 0; i < 7; i++) {
      const d = new Date(monday);
      d.setDate(monday.getDate() + i);
      const iso = formatIsoDate(d);

      const compareDate = new Date(d);
      compareDate.setHours(0, 0, 0, 0);

      const isToday = iso === todayIso;
      const isFuture = compareDate > today;
      const isPast = compareDate < today;

      days.push({
        dateIso: iso,
        dayNum: d.getDate(),
        dayLabel: d.toLocaleDateString('en-US', { weekday: 'narrow' }),
        isPast,
        isToday,
        isFuture,
      });
    }
    return days;
  }, [selectedDateObj, todayIso]);

  // Navigate back/forward by 1 day
  const handlePrevDay = () => {
    triggerHaptic('light');
    const prev = new Date(selectedDateObj);
    prev.setDate(prev.getDate() - 1);
    setSelectedDate(formatIsoDate(prev));
  };

  const handleNextDay = () => {
    triggerHaptic('light');
    const next = new Date(selectedDateObj);
    next.setDate(next.getDate() + 1);
    const nextIso = formatIsoDate(next);
    if (nextIso <= todayIso) {
      setSelectedDate(nextIso);
    }
  };

  const isToday = selectedDate === todayIso;
  const isFutureDate = selectedDate > todayIso;

  // Filter entries for the selected day
  const dayMeals = useMemo(() => {
    return meals.filter((m) => m.date === selectedDate);
  }, [meals, selectedDate]);

  const daySymptoms = useMemo(() => {
    return symptoms.filter((s) => s.date === selectedDate);
  }, [symptoms, selectedDate]);

  const dayExercises = useMemo(() => {
    return (exercises || []).filter((e) => e.date === selectedDate);
  }, [exercises, selectedDate]);

  // Categorize items into 3 periods:
  // Morning: 05:00 - 11:00
  // Midday:  11:00 - 17:00
  // Evening: 17:00 - 04:59
  const { morningItems, middayItems, eveningItems } = useMemo(() => {
    const getHour = (iso: string) => {
      try {
        const d = new Date(iso);
        return isNaN(d.getTime()) ? 12 : d.getHours();
      } catch {
        return 12;
      }
    };

    const getPeriod = (hour: number, mealType?: MealType): TimelinePeriod => {
      // Prioritize chronological hour so items at 10:00 AM always go to Morning
      if (hour >= 5 && hour < 11) return 'morning';
      if (hour >= 11 && hour < 17) return 'midday';
      if (hour >= 17 || hour < 5) return 'evening';
      if (mealType === 'Breakfast') return 'morning';
      if (mealType === 'Lunch') return 'midday';
      return 'evening';
    };

    const morning: PeriodItem[] = [];
    const midday: PeriodItem[] = [];
    const evening: PeriodItem[] = [];

    // Filter by type if active
    if (filterType === 'all' || filterType === 'meals') {
      for (const m of dayMeals) {
        const h = getHour(m.timestamp_iso);
        const p = getPeriod(h, m.meal_type);
        const item: PeriodItem = {
          id: m.id,
          type: 'meal',
          timeMs: new Date(m.timestamp_iso).getTime() || 0,
          timeStr: formatTime(m.timestamp_iso),
          mealData: m,
        };
        if (p === 'morning') morning.push(item);
        else if (p === 'midday') midday.push(item);
        else evening.push(item);
      }
    }

    if (filterType === 'all' || filterType === 'symptoms') {
      for (const s of daySymptoms) {
        const h = getHour(s.timestamp_iso);
        const p = getPeriod(h);
        const isStool = s.symptom_tags?.includes('Bowel Movement') || (s.stool_type !== null && s.stool_type !== undefined);
        const item: PeriodItem = {
          id: s.id,
          type: isStool ? 'stool' : 'symptom',
          timeMs: new Date(s.timestamp_iso).getTime() || 0,
          timeStr: formatTime(s.timestamp_iso),
          symptomData: s,
        };
        if (p === 'morning') morning.push(item);
        else if (p === 'midday') midday.push(item);
        else evening.push(item);
      }

      for (const e of dayExercises) {
        const h = getHour(e.timestamp_iso);
        const p = getPeriod(h);
        const item: PeriodItem = {
          id: e.id,
          type: 'exercise',
          timeMs: new Date(e.timestamp_iso).getTime() || 0,
          timeStr: formatTime(e.timestamp_iso),
          exerciseData: e,
        };
        if (p === 'morning') morning.push(item);
        else if (p === 'midday') midday.push(item);
        else evening.push(item);
      }
    }

    // Sort chronologically ascending within each period
    morning.sort((a, b) => a.timeMs - b.timeMs);
    midday.sort((a, b) => a.timeMs - b.timeMs);
    evening.sort((a, b) => a.timeMs - b.timeMs);

    return { morningItems: morning, middayItems: midday, eveningItems: evening };
  }, [dayMeals, daySymptoms, dayExercises, filterType]);

  // Check if standard meals have been logged for prompts
  const hasBreakfast = dayMeals.some((m) => m.meal_type === 'Breakfast');
  const hasLunch = dayMeals.some((m) => m.meal_type === 'Lunch');
  const hasDinner = dayMeals.some((m) => m.meal_type === 'Dinner');

  const isMorningSkipped = skippedSlots[`${selectedDate}_breakfast`];
  const isLunchSkipped = skippedSlots[`${selectedDate}_lunch`];
  const isDinnerSkipped = skippedSlots[`${selectedDate}_dinner`];

  const handleSkipMeal = (slot: string) => {
    setSkippedSlots((prev) => ({ ...prev, [`${selectedDate}_${slot}`]: true }));
  };

  // Formatted date label for top box (e.g., "Today, 14 Sep" or "Mon, 14 Sep")
  const dateBoxLabel = useMemo(() => {
    if (selectedDate === todayIso) {
      return `Today, ${selectedDateObj.toLocaleDateString([], { day: 'numeric', month: 'short' })}`;
    }
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    if (selectedDate === formatIsoDate(yesterday)) {
      return `Yesterday, ${selectedDateObj.toLocaleDateString([], { day: 'numeric', month: 'short' })}`;
    }
    return selectedDateObj.toLocaleDateString([], { weekday: 'short', day: 'numeric', month: 'short' });
  }, [selectedDate, selectedDateObj, todayIso]);

  // Global search filtered results
  const searchResults = useMemo<{ meals: Meal[]; symptoms: Symptom[] }>(() => {
    if (!searchQuery.trim()) return { meals: [], symptoms: [] };
    const q = searchQuery.toLowerCase();
    const matchedMeals = meals.filter(
      (m) =>
        m.description.toLowerCase().includes(q) ||
        m.ingredients_tags.toLowerCase().includes(q) ||
        (m.notes && m.notes.toLowerCase().includes(q))
    );
    const matchedSymptoms = symptoms.filter(
      (s) =>
        s.symptom_tags.toLowerCase().includes(q) ||
        (s.notes && s.notes.toLowerCase().includes(q))
    );
    return { meals: matchedMeals.slice(0, 15), symptoms: matchedSymptoms.slice(0, 15) };
  }, [searchQuery, meals, symptoms]);

  return (
    <div className="flex flex-col min-h-[calc(100vh-140px)] pb-28 pt-1 text-left select-none">
      {/* 1. Date Navigation Bar (< [ Date Box ] >) */}
      <div className="flex items-center justify-between gap-3 mb-2.5">
        <button
          type="button"
          onClick={handlePrevDay}
          className="w-10 h-10 rounded-2xl bg-white dark:bg-[#14161F] border border-slate-200/80 dark:border-zinc-800/80 flex items-center justify-center text-slate-800 dark:text-zinc-200 shadow-sm active:scale-95 transition-all shrink-0"
          aria-label="Previous Day"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>

        <button
          type="button"
          onClick={openFullCalendar}
          className="flex-1 h-10 px-4 rounded-2xl bg-white dark:bg-[#14161F] border border-slate-200/80 dark:border-zinc-800/80 flex items-center justify-center space-x-2 text-slate-950 dark:text-white font-bold text-xs sm:text-sm tracking-tight shadow-sm active:scale-[0.98] transition-all"
        >
          <CalendarIcon className="w-4 h-4 text-slate-500 dark:text-zinc-400" />
          <span>{dateBoxLabel}</span>
        </button>

        <button
          type="button"
          onClick={handleNextDay}
          disabled={isToday || isFutureDate}
          className={`w-10 h-10 rounded-2xl bg-white dark:bg-[#14161F] border border-slate-200/80 dark:border-zinc-800/80 flex items-center justify-center text-slate-800 dark:text-zinc-200 shadow-sm active:scale-95 transition-all shrink-0 ${
            isToday || isFutureDate ? 'opacity-30 cursor-not-allowed' : ''
          }`}
          aria-label="Next Day"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {/* 2. 7-Day Week Strip (Monday to Sunday) */}
      <div className="grid grid-cols-7 gap-1.5 mb-3.5">
        {weekDays.map((d) => {
          const isSelected = d.dateIso === selectedDate;
          return (
            <button
              key={d.dateIso}
              type="button"
              disabled={d.isFuture}
              onClick={() => {
                triggerHaptic('selection');
                setSelectedDate(d.dateIso);
              }}
              className={`flex flex-col items-center py-2 px-1 rounded-2xl transition-all duration-150 ${
                isSelected
                  ? 'bg-slate-950 dark:bg-white text-white dark:text-black shadow-md font-black scale-[1.02]'
                  : d.isFuture
                  ? 'opacity-35 cursor-not-allowed bg-slate-100/50 dark:bg-white/5 text-slate-400 dark:text-zinc-600'
                  : 'bg-white dark:bg-[#14161F] border border-slate-200/70 dark:border-zinc-800/80 text-slate-700 dark:text-zinc-300 hover:border-slate-400 dark:hover:border-zinc-700 active:scale-95'
              }`}
            >
              <span className="text-[10px] uppercase font-semibold tracking-tight">{d.dayLabel}</span>
              <span className="text-sm font-black mt-0.5">{d.dayNum}</span>
              {d.isToday && !isSelected && (
                <span className="w-1 h-1 rounded-full bg-emerald-500 mt-0.5" />
              )}
            </button>
          );
        })}
      </div>

      {/* 3. Inline Quick Filter Pills */}
      <div className="flex items-center justify-between mb-3 px-1">
        <div className="flex items-center space-x-1.5 bg-slate-200/60 dark:bg-zinc-800/60 p-1 rounded-xl">
          <button
            type="button"
            onClick={() => setFilterType('all')}
            className={`px-3 py-1 rounded-lg text-xs font-bold transition-all ${
              filterType === 'all'
                ? 'bg-white dark:bg-zinc-900 text-slate-950 dark:text-white shadow-xs'
                : 'text-slate-500 dark:text-zinc-400 hover:text-slate-900 dark:hover:text-white'
            }`}
          >
            All
          </button>
          <button
            type="button"
            onClick={() => setFilterType('meals')}
            className={`px-3 py-1 rounded-lg text-xs font-bold transition-all ${
              filterType === 'meals'
                ? 'bg-white dark:bg-zinc-900 text-slate-950 dark:text-white shadow-xs'
                : 'text-slate-500 dark:text-zinc-400 hover:text-slate-900 dark:hover:text-white'
            }`}
          >
            Meals
          </button>
          <button
            type="button"
            onClick={() => setFilterType('symptoms')}
            className={`px-3 py-1 rounded-lg text-xs font-bold transition-all ${
              filterType === 'symptoms'
                ? 'bg-white dark:bg-zinc-900 text-slate-950 dark:text-white shadow-xs'
                : 'text-slate-500 dark:text-zinc-400 hover:text-slate-900 dark:hover:text-white'
            }`}
          >
            Activity
          </button>
        </div>

        {filterType === 'symptoms' && (
          <button
            type="button"
            onClick={() => onOpenLogSymptom?.()}
            className="px-3 py-1.5 rounded-xl bg-slate-950 dark:bg-white text-white dark:text-black text-xs font-bold shadow-xs active:scale-95 transition-all flex items-center space-x-1"
          >
            <Plus className="w-3.5 h-3.5" />
            <span>Log Activity</span>
          </button>
        )}
      </div>

      {/* 4. Dynamic 3-Part Day Layout (Morning, Midday, Evening) */}
      <div className="flex-1 flex flex-col gap-3.5 w-full">
        {/* === SECTION 1: MORNING (05:00 - 11:00) === */}
        <div className="flex-1 min-h-[160px] p-4 rounded-3xl bg-white dark:bg-[#14161F] border border-slate-200/80 dark:border-zinc-800/80 shadow-xs flex flex-col justify-between transition-all">
          <div>
            <div className="flex items-center justify-between pb-2 mb-2 border-b border-slate-100 dark:border-zinc-800/80">
              <div className="flex items-center space-x-2">
                <Sunrise className="w-4 h-4 text-slate-700 dark:text-zinc-300" />
                <h2 className="text-xs font-black uppercase tracking-wider text-slate-800 dark:text-zinc-200">
                  Morning
                </h2>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-[10px] text-slate-400 dark:text-zinc-500 font-medium">05:00 – 11:00</span>
                {filterType !== 'symptoms' && (
                  <button
                    type="button"
                    onClick={() => (onOpenQuickAdd ? onOpenQuickAdd('Breakfast') : onOpenLogMeal?.('Breakfast'))}
                    className="px-2 py-0.5 rounded-lg bg-slate-100 hover:bg-slate-200 dark:bg-zinc-800 dark:hover:bg-zinc-700 text-slate-700 dark:text-zinc-300 text-[11px] font-semibold flex items-center space-x-1 transition-colors"
                    title="Add morning entry"
                  >
                    <Plus className="w-3 h-3" />
                    <span>Add</span>
                  </button>
                )}
              </div>
            </div>

            {/* Render items in morning */}
            <div className="space-y-2">
              {morningItems.map((item) => (
                <TimelineEntryRow
                  key={item.id}
                  item={item}
                  isFodmapEnabled={isFodmapEnabled}
                  onEditMeal={onEditMeal}
                  onEditSymptom={onEditSymptom}
                  onEditExercise={onEditExercise}
                  onDeleteMeal={onDeleteMeal}
                  onDeleteSymptom={onDeleteSymptom}
                  onDeleteExercise={onDeleteExercise}
                />
              ))}

              {/* Single-Line Missing Breakfast Prompt */}
              {!hasBreakfast && !isMorningSkipped && filterType !== 'symptoms' && (
                <div className="px-3.5 py-2.5 rounded-2xl bg-slate-50 dark:bg-zinc-900/40 border border-dashed border-slate-200 dark:border-zinc-800/80 flex items-center justify-between">
                  <span className="text-xs font-bold text-slate-800 dark:text-zinc-200 truncate">
                    Did you have breakfast?
                  </span>
                  <div className="flex items-center space-x-2 shrink-0 ml-2">
                    <button
                      type="button"
                      onClick={() => handleSkipMeal('breakfast')}
                      className="px-2 py-1 text-[11px] font-semibold text-slate-400 hover:text-slate-600 dark:text-zinc-500 dark:hover:text-zinc-300 transition-colors"
                    >
                      Skip
                    </button>
                    <button
                      type="button"
                      onClick={() => (onOpenQuickAdd ? onOpenQuickAdd('Breakfast') : onOpenLogMeal?.('Breakfast'))}
                      className="px-3 py-1 rounded-xl bg-slate-950 dark:bg-white text-white dark:text-black font-bold text-[11px] active:scale-95 transition-all shadow-xs"
                    >
                      + Log
                    </button>
                  </div>
                </div>
              )}

              {/* Activity Log Prompt if Empty in Activity View */}
              {filterType === 'symptoms' && morningItems.length === 0 && (
                <div className="px-3.5 py-2.5 rounded-2xl bg-slate-50 dark:bg-zinc-900/40 border border-dashed border-slate-200 dark:border-zinc-800/80 flex items-center justify-between">
                  <span className="text-xs font-medium text-slate-500 dark:text-zinc-400">
                    No morning symptoms or activity
                  </span>
                  <button
                    type="button"
                    onClick={() => onOpenLogSymptom?.('Next-Morning')}
                    className="px-2.5 py-1 rounded-xl bg-slate-950 dark:bg-white text-white dark:text-black font-bold text-[11px] active:scale-95 transition-all shadow-xs"
                  >
                    + Log
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* === SECTION 2: MIDDAY (11:00 - 17:00) === */}
        <div className="flex-1 min-h-[160px] p-4 rounded-3xl bg-white dark:bg-[#14161F] border border-slate-200/80 dark:border-zinc-800/80 shadow-xs flex flex-col justify-between transition-all">
          <div>
            <div className="flex items-center justify-between pb-2 mb-2 border-b border-slate-100 dark:border-zinc-800/80">
              <div className="flex items-center space-x-2">
                <Sun className="w-4 h-4 text-slate-700 dark:text-zinc-300" />
                <h2 className="text-xs font-black uppercase tracking-wider text-slate-800 dark:text-zinc-200">
                  Midday
                </h2>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-[10px] text-slate-400 dark:text-zinc-500 font-medium">11:00 – 17:00</span>
                {filterType !== 'symptoms' && (
                  <button
                    type="button"
                    onClick={() => (onOpenQuickAdd ? onOpenQuickAdd('Lunch') : onOpenLogMeal?.('Lunch'))}
                    className="px-2 py-0.5 rounded-lg bg-slate-100 hover:bg-slate-200 dark:bg-zinc-800 dark:hover:bg-zinc-700 text-slate-700 dark:text-zinc-300 text-[11px] font-semibold flex items-center space-x-1 transition-colors"
                    title="Add midday entry"
                  >
                    <Plus className="w-3 h-3" />
                    <span>Add</span>
                  </button>
                )}
              </div>
            </div>

            {/* Render items in midday */}
            <div className="space-y-2">
              {middayItems.map((item) => (
                <TimelineEntryRow
                  key={item.id}
                  item={item}
                  isFodmapEnabled={isFodmapEnabled}
                  onEditMeal={onEditMeal}
                  onEditSymptom={onEditSymptom}
                  onEditExercise={onEditExercise}
                  onDeleteMeal={onDeleteMeal}
                  onDeleteSymptom={onDeleteSymptom}
                  onDeleteExercise={onDeleteExercise}
                />
              ))}

              {/* Single-Line Missing Lunch Prompt */}
              {!hasLunch && !isLunchSkipped && filterType !== 'symptoms' && (
                <div className="px-3.5 py-2.5 rounded-2xl bg-slate-50 dark:bg-zinc-900/40 border border-dashed border-slate-200 dark:border-zinc-800/80 flex items-center justify-between">
                  <span className="text-xs font-bold text-slate-800 dark:text-zinc-200 truncate">
                    Did you have lunch?
                  </span>
                  <div className="flex items-center space-x-2 shrink-0 ml-2">
                    <button
                      type="button"
                      onClick={() => handleSkipMeal('lunch')}
                      className="px-2 py-1 text-[11px] font-semibold text-slate-400 hover:text-slate-600 dark:text-zinc-500 dark:hover:text-zinc-300 transition-colors"
                    >
                      Skip
                    </button>
                    <button
                      type="button"
                      onClick={() => (onOpenQuickAdd ? onOpenQuickAdd('Lunch') : onOpenLogMeal?.('Lunch'))}
                      className="px-3 py-1 rounded-xl bg-slate-950 dark:bg-white text-white dark:text-black font-bold text-[11px] active:scale-95 transition-all shadow-xs"
                    >
                      + Log
                    </button>
                  </div>
                </div>
              )}

              {/* Activity Log Prompt if Empty in Activity View */}
              {filterType === 'symptoms' && middayItems.length === 0 && (
                <div className="px-3.5 py-2.5 rounded-2xl bg-slate-50 dark:bg-zinc-900/40 border border-dashed border-slate-200 dark:border-zinc-800/80 flex items-center justify-between">
                  <span className="text-xs font-medium text-slate-500 dark:text-zinc-400">
                    No midday symptoms or activity
                  </span>
                  <button
                    type="button"
                    onClick={() => onOpenLogSymptom?.('Post-Meal')}
                    className="px-2.5 py-1 rounded-xl bg-slate-950 dark:bg-white text-white dark:text-black font-bold text-[11px] active:scale-95 transition-all shadow-xs"
                  >
                    + Log
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* === SECTION 3: EVENING (17:00 - 04:59) === */}
        <div className="flex-1 min-h-[160px] p-4 rounded-3xl bg-white dark:bg-[#14161F] border border-slate-200/80 dark:border-zinc-800/80 shadow-xs flex flex-col justify-between transition-all">
          <div>
            <div className="flex items-center justify-between pb-2 mb-2 border-b border-slate-100 dark:border-zinc-800/80">
              <div className="flex items-center space-x-2">
                <Moon className="w-4 h-4 text-slate-700 dark:text-zinc-300" />
                <h2 className="text-xs font-black uppercase tracking-wider text-slate-800 dark:text-zinc-200">
                  Evening
                </h2>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-[10px] text-slate-400 dark:text-zinc-500 font-medium">17:00 – 04:59</span>
                {filterType !== 'symptoms' && (
                  <button
                    type="button"
                    onClick={() => (onOpenQuickAdd ? onOpenQuickAdd('Dinner') : onOpenLogMeal?.('Dinner'))}
                    className="px-2 py-0.5 rounded-lg bg-slate-100 hover:bg-slate-200 dark:bg-zinc-800 dark:hover:bg-zinc-700 text-slate-700 dark:text-zinc-300 text-[11px] font-semibold flex items-center space-x-1 transition-colors"
                    title="Add evening entry"
                  >
                    <Plus className="w-3 h-3" />
                    <span>Add</span>
                  </button>
                )}
              </div>
            </div>

            {/* Render items in evening */}
            <div className="space-y-2">
              {eveningItems.map((item) => (
                <TimelineEntryRow
                  key={item.id}
                  item={item}
                  isFodmapEnabled={isFodmapEnabled}
                  onEditMeal={onEditMeal}
                  onEditSymptom={onEditSymptom}
                  onEditExercise={onEditExercise}
                  onDeleteMeal={onDeleteMeal}
                  onDeleteSymptom={onDeleteSymptom}
                  onDeleteExercise={onDeleteExercise}
                />
              ))}

              {/* Single-Line Missing Dinner Prompt */}
              {!hasDinner && !isDinnerSkipped && filterType !== 'symptoms' && (
                <div className="px-3.5 py-2.5 rounded-2xl bg-slate-50 dark:bg-zinc-900/40 border border-dashed border-slate-200 dark:border-zinc-800/80 flex items-center justify-between">
                  <span className="text-xs font-bold text-slate-800 dark:text-zinc-200 truncate">
                    Did you have dinner?
                  </span>
                  <div className="flex items-center space-x-2 shrink-0 ml-2">
                    <button
                      type="button"
                      onClick={() => handleSkipMeal('dinner')}
                      className="px-2 py-1 text-[11px] font-semibold text-slate-400 hover:text-slate-600 dark:text-zinc-500 dark:hover:text-zinc-300 transition-colors"
                    >
                      Skip
                    </button>
                    <button
                      type="button"
                      onClick={() => (onOpenQuickAdd ? onOpenQuickAdd('Dinner') : onOpenLogMeal?.('Dinner'))}
                      className="px-3 py-1 rounded-xl bg-slate-950 dark:bg-white text-white dark:text-black font-bold text-[11px] active:scale-95 transition-all shadow-xs"
                    >
                      + Log
                    </button>
                  </div>
                </div>
              )}

              {/* Activity Log Prompt if Empty in Activity View */}
              {filterType === 'symptoms' && eveningItems.length === 0 && (
                <div className="px-3.5 py-2.5 rounded-2xl bg-slate-50 dark:bg-zinc-900/40 border border-dashed border-slate-200 dark:border-zinc-800/80 flex items-center justify-between">
                  <span className="text-xs font-medium text-slate-500 dark:text-zinc-400">
                    No evening symptoms or activity
                  </span>
                  <button
                    type="button"
                    onClick={() => onOpenLogSymptom?.('General Daily')}
                    className="px-2.5 py-1 rounded-xl bg-slate-950 dark:bg-white text-white dark:text-black font-bold text-[11px] active:scale-95 transition-all shadow-xs"
                  >
                    + Log
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Calendar Full Month Picker Modal */}
      {showCalendar && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-24 sm:pt-28 px-4 animate-fade-in pointer-events-none">
          {/* Transparent click-outside backdrop */}
          <div
            className="fixed inset-0 pointer-events-auto"
            onClick={() => {
              triggerHaptic('light');
              setShowCalendar(false);
            }}
          />

          {/* Floating Calendar Card with drop shadow */}
          <div className="relative z-10 w-full max-w-sm bg-white dark:bg-[#14161F] border border-slate-200/90 dark:border-zinc-800 rounded-[28px] p-5 shadow-[0_25px_60px_-15px_rgba(0,0,0,0.3)] dark:shadow-[0_25px_60px_-15px_rgba(0,0,0,0.8)] space-y-3.5 pointer-events-auto">
            {/* Header: Month title, Month arrows & Close */}
            <div className="flex items-center justify-between pb-3 border-b border-slate-100 dark:border-zinc-800/80">
              <h3 className="text-base font-black text-slate-950 dark:text-white tracking-tight">
                {monthNames[calendarViewMonth]} {calendarViewYear}
              </h3>
              <div className="flex items-center space-x-1.5">
                <button
                  type="button"
                  onClick={handlePrevMonth}
                  className="w-8 h-8 rounded-xl bg-slate-100 dark:bg-zinc-800 flex items-center justify-center text-slate-700 dark:text-zinc-300 hover:bg-slate-200 dark:hover:bg-zinc-700 active:scale-90 transition-all"
                  aria-label="Previous Month"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={handleNextMonth}
                  className="w-8 h-8 rounded-xl bg-slate-100 dark:bg-zinc-800 flex items-center justify-center text-slate-700 dark:text-zinc-300 hover:bg-slate-200 dark:hover:bg-zinc-700 active:scale-90 transition-all"
                  aria-label="Next Month"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => {
                    triggerHaptic('light');
                    setShowCalendar(false);
                  }}
                  className="w-8 h-8 rounded-full bg-slate-100 dark:bg-zinc-800 flex items-center justify-center text-slate-500 dark:text-zinc-400 hover:bg-slate-200 dark:hover:bg-zinc-700 active:scale-90 transition-all ml-1"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Weekday headers: M T W T F S S */}
            <div className="grid grid-cols-7 gap-1 text-center">
              {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((day, idx) => (
                <span key={idx} className="text-[11px] font-bold text-slate-400 dark:text-zinc-500 uppercase py-0.5">
                  {day}
                </span>
              ))}
            </div>

            {/* Days Grid */}
            <div className="grid grid-cols-7 gap-1">
              {/* Blank cells for offset */}
              {Array.from({ length: startDayOfWeek }).map((_, idx) => (
                <div key={`blank-${idx}`} className="h-10 rounded-xl" />
              ))}

              {/* Day numbers */}
              {Array.from({ length: daysInCalendarMonth }).map((_, idx) => {
                const dayNum = idx + 1;
                const padMonth = String(calendarViewMonth + 1).padStart(2, '0');
                const padDay = String(dayNum).padStart(2, '0');
                const dateStr = `${calendarViewYear}-${padMonth}-${padDay}`;

                const isSelected = selectedDate === dateStr;
                const isCurrentToday = dateStr === todayIso;
                const isFuture = dateStr > todayIso;

                const dayMeals = mealsByDate.get(dateStr) || [];
                const daySymptoms = symptomsByDate.get(dateStr) || [];

                const hasSpike = daySymptoms.some(
                  (s) => s.bloating_score >= 3 || s.soreness_score >= 3 || (s.stool_type && (s.stool_type <= 2 || s.stool_type >= 5))
                );
                const hasComfortable = dayMeals.length > 0 && !hasSpike;

                return (
                  <button
                    key={dateStr}
                    type="button"
                    disabled={isFuture}
                    onClick={() => {
                      if (isFuture) return;
                      triggerHaptic('selection');
                      setSelectedDate(dateStr);
                      setShowCalendar(false);
                    }}
                    className={`h-10 rounded-xl flex flex-col items-center justify-between p-1 transition-all relative ${
                      isSelected
                        ? 'bg-slate-950 dark:bg-white text-white dark:text-black font-black shadow-md scale-105 z-10'
                        : isCurrentToday
                        ? 'bg-slate-100 dark:bg-zinc-800 text-slate-950 dark:text-white border-2 border-emerald-500/80 font-bold'
                        : isFuture
                        ? 'opacity-25 cursor-not-allowed text-slate-400 dark:text-zinc-600'
                        : 'bg-slate-50 dark:bg-zinc-900/60 hover:bg-slate-100 dark:hover:bg-zinc-800 text-slate-700 dark:text-zinc-300 border border-slate-200/50 dark:border-zinc-800/60'
                    }`}
                  >
                    <span className="text-xs leading-none mt-0.5">{dayNum}</span>

                    {/* Dot Indicators */}
                    <div className="flex items-center space-x-0.5 mb-0.5">
                      {hasSpike && (
                        <span className={`w-1.5 h-1.5 rounded-full ${isSelected ? 'bg-rose-300' : 'bg-rose-500'}`} />
                      )}
                      {hasComfortable && (
                        <span className={`w-1.5 h-1.5 rounded-full ${isSelected ? 'bg-emerald-300' : 'bg-emerald-500'}`} />
                      )}
                      {!hasSpike && !hasComfortable && dayMeals.length > 0 && (
                        <span className={`w-1.5 h-1.5 rounded-full ${isSelected ? 'bg-slate-300' : 'bg-slate-400 dark:bg-zinc-500'}`} />
                      )}
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Footer Buttons */}
            <div className="flex items-center space-x-2 pt-2 border-t border-slate-100 dark:border-zinc-800/80">
              <button
                type="button"
                onClick={handleJumpTodayInCalendar}
                className="flex-1 py-2.5 rounded-xl bg-slate-100 dark:bg-zinc-800 text-slate-800 dark:text-zinc-200 font-bold text-xs active:scale-95 transition-all"
              >
                Jump to Today
              </button>
              <button
                type="button"
                onClick={() => {
                  triggerHaptic('light');
                  setShowCalendar(false);
                }}
                className="flex-1 py-2.5 rounded-xl bg-slate-950 dark:bg-white text-white dark:text-black font-bold text-xs active:scale-95 transition-all"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Global Search Drawer */}
      {showSearch && (
        <div className="fixed inset-0 z-50 flex flex-col bg-white dark:bg-[#0D0E11] animate-fade-in p-5 max-w-md mx-auto">
          <div className="flex items-center justify-between pb-3 border-b border-slate-100 dark:border-zinc-800">
            <h3 className="text-base font-black text-slate-950 dark:text-white uppercase tracking-tight">
              Search Timeline
            </h3>
            <button
              type="button"
              onClick={() => {
                setShowSearch(false);
                setSearchQuery('');
              }}
              className="w-8 h-8 rounded-full bg-slate-100 dark:bg-zinc-800 flex items-center justify-center text-slate-500 dark:text-zinc-400"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="relative my-3">
            <Search className="w-4 h-4 text-slate-400 dark:text-zinc-500 absolute left-3.5 top-3.5" />
            <input
              type="text"
              placeholder="Search dishes, ingredients, or notes..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              autoFocus
              className="w-full pl-10 pr-4 py-3 bg-slate-100 dark:bg-[#14161F] border border-slate-200 dark:border-zinc-800/80 rounded-2xl text-xs font-semibold text-slate-950 dark:text-white placeholder-slate-400 dark:placeholder-zinc-500 focus:outline-none"
            />
          </div>

          <div className="flex-1 overflow-y-auto space-y-2 pt-2">
            {searchQuery.trim() && searchResults.meals?.length === 0 && searchResults.symptoms?.length === 0 && (
              <p className="text-center text-xs text-slate-400 dark:text-zinc-500 py-8">
                No entries match "{searchQuery}"
              </p>
            )}

            {searchResults.meals?.map((m) => (
              <div
                key={m.id}
                onClick={() => {
                  setSelectedDate(m.date);
                  setShowSearch(false);
                  onEditMeal?.(m);
                }}
                className="p-3 rounded-2xl bg-white dark:bg-[#14161F] border border-slate-200/80 dark:border-zinc-800/80 cursor-pointer active:scale-[0.99] transition-all"
              >
                <div className="flex items-center justify-between text-[11px] text-slate-500 dark:text-zinc-400 mb-1">
                  <span className="font-bold text-slate-900 dark:text-white">{m.meal_type} · {m.date}</span>
                  <span>{formatTime(m.timestamp_iso)}</span>
                </div>
                <p className="text-xs font-semibold text-slate-800 dark:text-zinc-200">{m.description}</p>
                {m.ingredients_tags && (
                  <p className="text-[10px] text-slate-400 dark:text-zinc-500 truncate mt-1">
                    {m.ingredients_tags}
                  </p>
                )}
              </div>
            ))}

            {searchResults.symptoms?.map((s) => {
              const largest = getLargestSymptom(s);
              return (
                <div
                  key={s.id}
                  onClick={() => {
                    setSelectedDate(s.date);
                    setShowSearch(false);
                    onEditSymptom?.(s);
                  }}
                  className="p-3 rounded-2xl bg-white dark:bg-[#14161F] border border-slate-200/80 dark:border-zinc-800/80 cursor-pointer active:scale-[0.99] transition-all"
                >
                  <div className="flex items-center justify-between text-[11px] text-slate-500 dark:text-zinc-400 mb-1">
                    <span className="font-bold text-slate-900 dark:text-white">Check-in · {s.date}</span>
                    <span>{formatTime(s.timestamp_iso)}</span>
                  </div>
                  <p className="text-xs font-semibold text-slate-800 dark:text-zinc-200">
                    {largest.score > 0 ? `${largest.name} (${largest.score}/10)` : 'All Clear (0/10)'}
                    {s.stool_type ? ` · Stool T${s.stool_type}` : ''}
                  </p>
                  {s.notes && (
                    <p className="text-[10px] text-slate-400 dark:text-zinc-500 italic mt-0.5 truncate">
                      {s.notes}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
});

// Helper to find the single highest-scoring symptom from 0-10 scale
export function getLargestSymptom(s: Symptom): { name: string; score: number } {
  let highestName = '';
  let highestScore = 0;

  if (s.symptom_scores && Object.keys(s.symptom_scores).length > 0) {
    for (const sym of MAIN_SYMPTOMS) {
      const score = s.symptom_scores[sym.id] ?? 0;
      if (score > highestScore) {
        highestScore = score;
        highestName = sym.name;
      }
    }
  }

  if (highestScore === 0) {
    const b = s.bloating_score ?? 0;
    const p = s.soreness_score ?? 0;
    if (b > 0 && b >= p) {
      highestScore = b;
      highestName = 'Bloating';
    } else if (p > 0) {
      highestScore = p;
      highestName = 'Stomach Pain';
    }
  }

  return { name: highestName, score: highestScore };
}

// === COMPONENT: TimelineEntryRow ===
// Renders prominent cards for meals, and sleek thinner pills for check-ins/stool/exercise
const TimelineEntryRow: React.FC<{
  item: PeriodItem;
  isFodmapEnabled?: boolean;
  onEditMeal?: (meal: Meal) => void;
  onEditSymptom?: (symptom: Symptom) => void;
  onEditExercise?: (exercise: Exercise) => void;
  onDeleteMeal?: (id: string) => void;
  onDeleteSymptom?: (id: string) => void;
  onDeleteExercise?: (id: string) => void;
}> = ({
  item,
  isFodmapEnabled = true,
  onEditMeal,
  onEditSymptom,
  onEditExercise,
  onDeleteMeal,
  onDeleteSymptom,
  onDeleteExercise,
}) => {
  const [confirmDelete, setConfirmDelete] = useState(false);

  if (item.type === 'meal' && item.mealData) {
    const meal = item.mealData;
    const { rating: fodmapRating, dominantCulprit } = getMealFodmapReport(meal);
    const MealIcon = meal.meal_type === 'Drink' ? Coffee : Utensils;

    const fodmapStyles = isFodmapEnabled
      ? {
          low: {
            badge: 'bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 border-emerald-300/80 dark:border-emerald-500/40',
            pill: 'bg-emerald-100 dark:bg-emerald-500/20 text-emerald-800 dark:text-emerald-300',
          },
          moderate: {
            badge: 'bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-300 border-amber-300/80 dark:border-amber-500/40',
            pill: 'bg-amber-100 dark:bg-amber-500/20 text-amber-800 dark:text-amber-300',
          },
          high: {
            badge: 'bg-rose-100 dark:bg-rose-500/20 text-rose-700 dark:text-rose-300 border-rose-300/80 dark:border-rose-500/40',
            pill: 'bg-rose-100 dark:bg-rose-500/20 text-rose-800 dark:text-rose-300',
          },
        }[fodmapRating]
      : {
          badge: 'bg-slate-100 dark:bg-zinc-800 text-slate-700 dark:text-zinc-300 border-slate-200 dark:border-zinc-700',
          pill: 'hidden',
        };

    return (
      <div
        onClick={() => onEditMeal?.(meal)}
        className="p-3 rounded-2xl bg-slate-50 dark:bg-zinc-900/40 border border-slate-200/80 dark:border-zinc-800/80 hover:border-slate-300 dark:hover:border-zinc-700 cursor-pointer active:scale-[0.99] transition-all relative group"
      >
        <div className="flex items-start space-x-2.5 min-w-0">
          {/* Meal Icon Indicator */}
          <div
            className={`p-1.5 rounded-lg border shrink-0 flex items-center justify-center shadow-2xs mt-0.5 ${fodmapStyles.badge}`}
            title={isFodmapEnabled ? `FODMAP: ${fodmapRating.toUpperCase()}` : meal.meal_type}
          >
            <MealIcon className="w-3.5 h-3.5" />
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between gap-1 mb-0.5">
              <div className="flex items-center space-x-1.5 flex-wrap">
                <span className="text-[11px] font-bold text-slate-700 dark:text-zinc-300">
                  {meal.meal_type}
                </span>
                {isFodmapEnabled && (
                  <span className={`text-[9px] font-extrabold uppercase px-1.5 py-0.5 rounded tracking-wider ${fodmapStyles.pill}`}>
                    {fodmapRating}
                  </span>
                )}
                {isFodmapEnabled && dominantCulprit && fodmapRating !== 'low' && (
                  <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-amber-50 dark:bg-amber-950/40 text-amber-800 dark:text-amber-300 border border-amber-200 dark:border-amber-800/60">
                    {dominantCulprit}
                  </span>
                )}
              </div>
              <div className="flex items-center space-x-2 shrink-0">
                <span className="text-[10px] text-slate-400 dark:text-zinc-500 font-medium">
                  {item.timeStr}
                </span>
                {onDeleteMeal && (
                  confirmDelete ? (
                    <div className="flex items-center space-x-1" onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDeleteMeal(meal.id);
                        }}
                        className="px-2 py-0.5 bg-rose-600 text-white text-[10px] font-bold rounded-md active:scale-95 transition-all"
                      >
                        Delete
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setConfirmDelete(false);
                        }}
                        className="px-1.5 py-0.5 bg-slate-200 dark:bg-zinc-700 text-slate-600 dark:text-zinc-300 text-[10px] font-bold rounded-md"
                      >
                        ✕
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setConfirmDelete(true);
                      }}
                      className="p-1 rounded-lg text-slate-400 hover:text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-950/30 transition-colors"
                      title="Delete Meal"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )
                )}
              </div>
            </div>

            <p className="text-xs sm:text-sm font-bold text-slate-950 dark:text-white leading-snug">
              {meal.description}
            </p>

            {meal.notes && (
              <p className="text-[10px] text-slate-400 dark:text-zinc-500 italic mt-1 truncate">
                {meal.notes}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Exercise: Render as a dedicated motility & activity card
  if (item.type === 'exercise' && item.exerciseData) {
    const e = item.exerciseData;
    const ActivityIconComponent = () => {
      const lower = (e.activity_type || '').toLowerCase();
      if (lower.includes('walk')) return <Footprints className="w-3.5 h-3.5" />;
      if (lower.includes('run')) return <Activity className="w-3.5 h-3.5" />;
      if (lower.includes('cycl') || lower.includes('bike')) return <Bike className="w-3.5 h-3.5" />;
      if (lower.includes('gym') || lower.includes('weight')) return <Dumbbell className="w-3.5 h-3.5" />;
      if (lower.includes('yoga') || lower.includes('stretch')) return <Sparkles className="w-3.5 h-3.5" />;
      if (lower.includes('swim')) return <Waves className="w-3.5 h-3.5" />;
      if (lower.includes('sport') || lower.includes('game')) return <Trophy className="w-3.5 h-3.5" />;
      return <Zap className="w-3.5 h-3.5" />;
    };

    return (
      <div
        onClick={() => onEditExercise?.(e)}
        className="px-3 py-2.5 rounded-xl bg-teal-50/50 dark:bg-teal-950/20 border border-teal-200/80 dark:border-teal-500/30 flex items-center justify-between cursor-pointer active:scale-[0.99] transition-all"
      >
        <div className="flex items-center space-x-2.5 min-w-0">
          <div className="w-7 h-7 rounded-lg bg-teal-100 dark:bg-teal-500/20 text-teal-700 dark:text-teal-300 flex items-center justify-center text-sm shadow-2xs shrink-0">
            <ActivityIconComponent />
          </div>

          <div className="min-w-0">
            <div className="flex items-center space-x-1.5 flex-wrap">
              <p className="text-xs font-bold text-slate-900 dark:text-white truncate">
                {e.activity_type} ({e.duration_minutes}m)
              </p>
              {e.timing_relative_to_meal && (
                <span className="text-[10px] px-1.5 py-0.5 rounded-md font-semibold bg-teal-100 dark:bg-teal-900/40 text-teal-800 dark:text-teal-300">
                  {e.timing_relative_to_meal}
                </span>
              )}
              {e.digestive_effect && (
                <span
                  className={`text-[10px] px-1.5 py-0.5 rounded-md font-semibold ${
                    e.digestive_effect.includes('Relieved') || e.digestive_effect.includes('Improved')
                      ? 'bg-emerald-100 dark:bg-emerald-900/50 text-emerald-800 dark:text-emerald-300'
                      : e.digestive_effect.includes('Cramps')
                      ? 'bg-amber-100 dark:bg-amber-900/50 text-amber-800 dark:text-amber-300'
                      : 'bg-slate-100 dark:bg-zinc-800 text-slate-600 dark:text-zinc-400'
                  }`}
                >
                  {e.digestive_effect}
                </span>
              )}
            </div>
            {e.notes && (
              <p className="text-[10px] text-slate-400 dark:text-zinc-500 italic truncate mt-0.5">
                {e.notes}
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center space-x-2 shrink-0 ml-2">
          <span className="text-[10px] text-slate-400 dark:text-zinc-500 font-medium">{item.timeStr}</span>
          {onDeleteExercise && (
            confirmDelete ? (
              <div className="flex items-center space-x-1" onClick={(e) => e.stopPropagation()}>
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    onDeleteExercise(e.id);
                  }}
                  className="px-2 py-0.5 bg-rose-600 text-white text-[10px] font-bold rounded-md active:scale-95 transition-all"
                >
                  Delete
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setConfirmDelete(false);
                  }}
                  className="px-1.5 py-0.5 bg-slate-200 dark:bg-zinc-700 text-slate-600 dark:text-zinc-300 text-[10px] font-bold rounded-md"
                >
                  ✕
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setConfirmDelete(true);
                }}
                className="p-1 rounded-lg text-slate-400 hover:text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-950/30 transition-colors"
                title="Delete Exercise"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )
          )}
        </div>
      </div>
    );
  }

  // Symptom or Stool: Render as a thin, sleek horizontal row/pill
  if (item.symptomData) {
    const s = item.symptomData;
    const isStool = item.type === 'stool';
    const largest = getLargestSymptom(s);

    const isSevere = isStool
      ? Boolean(s.stool_type && (s.stool_type <= 2 || s.stool_type >= 6))
      : largest.score >= 7;

    const isModerate = isStool
      ? Boolean(s.stool_type && (s.stool_type === 3 || s.stool_type === 5))
      : largest.score >= 3 && largest.score < 7;

    let accentBorder = 'border-emerald-200/80 dark:border-emerald-500/30';
    let accentBg = 'bg-emerald-50/40 dark:bg-emerald-950/20';
    let badgeColor = 'text-emerald-600 dark:text-emerald-400 bg-emerald-100 dark:bg-emerald-500/20';

    if (isSevere) {
      accentBorder = 'border-rose-300/80 dark:border-rose-500/30';
      accentBg = 'bg-rose-50/60 dark:bg-rose-950/30';
      badgeColor = 'text-rose-600 dark:text-rose-400 bg-rose-100 dark:bg-rose-500/20';
    } else if (isModerate) {
      accentBorder = 'border-amber-300/80 dark:border-amber-500/30';
      accentBg = 'bg-amber-50/60 dark:bg-amber-950/30';
      badgeColor = 'text-amber-600 dark:text-amber-400 bg-amber-100 dark:bg-amber-500/20';
    }

    return (
      <div
        onClick={() => onEditSymptom?.(s)}
        className={`px-3 py-2.5 rounded-xl ${accentBg} border ${accentBorder} flex items-center justify-between cursor-pointer active:scale-[0.99] transition-all`}
      >
        <div className="flex items-center space-x-2.5 min-w-0">
          {isStool ? (
            <div className={`p-1.5 rounded-lg ${badgeColor}`}>
              <Droplets className="w-3.5 h-3.5" />
            </div>
          ) : (
            <div className={`p-1.5 rounded-lg ${badgeColor} shadow-2xs`}>
              <Activity className="w-3.5 h-3.5" />
            </div>
          )}

          <div className="min-w-0">
            {isStool ? (
              <p className="text-xs font-bold text-slate-900 dark:text-white truncate">
                Stool Type {s.stool_type || '—'} {s.stool_type === 4 ? '(Optimal)' : ''}
              </p>
            ) : (
              <p className="text-xs font-bold text-slate-900 dark:text-white truncate">
                Check-in{largest.score > 0 ? `: ${largest.name} (${largest.score}/10)` : ' (All Clear)'}
              </p>
            )}
            {s.notes && (
              <p className="text-[10px] text-slate-400 dark:text-zinc-500 italic truncate">
                {s.notes}
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center space-x-2 shrink-0 ml-2">
          <span className="text-[10px] text-slate-400 dark:text-zinc-500 font-medium">{item.timeStr}</span>
          {onDeleteSymptom && (
            confirmDelete ? (
              <div className="flex items-center space-x-1" onClick={(e) => e.stopPropagation()}>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteSymptom(s.id);
                  }}
                  className="px-2 py-0.5 bg-rose-600 text-white text-[10px] font-bold rounded-md active:scale-95 transition-all"
                >
                  Delete
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setConfirmDelete(false);
                  }}
                  className="px-1.5 py-0.5 bg-slate-200 dark:bg-zinc-700 text-slate-600 dark:text-zinc-300 text-[10px] font-bold rounded-md"
                >
                  ✕
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setConfirmDelete(true);
                }}
                className="p-1 rounded-lg text-slate-400 hover:text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-950/30 transition-colors"
                title="Delete Entry"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )
          )}
        </div>
      </div>
    );
  }

  return null;
};
